import { useEffect, useMemo, useState } from 'react'
import { Route, Routes, useLocation, useParams } from 'react-router-dom'
import { startCase } from 'lodash-es'

import { IContinuousRoute } from '$/types'
import { useAppSelector } from 'app/hooks'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import {
  IGetQRATableQueryParams,
  useAddQRATableExportJobMutation,
  useGetQRATableQuery
} from 'app/services/api/qra/datatable'
import {
  IGetQRAGraphsQueryParams,
  useGetQRAGraphsQuery
} from 'app/services/api/qra/graphs'
import { selectSystemScopeIdContainer } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { useToggleRiskDensity } from 'common/hooks/useToggleRiskDensity'
import {
  getLineIdFromQRAViewBasePath,
  IQRANavUtilsConsumerComponentInitProps,
  QRAViewType
} from 'common/utils/navigation/qraNavUtils'

import { IQRAViewProps } from 'features/RiskManagement'
import {
  IGraphsViewBaseProps,
  IQRAGraphBools
} from 'features/RiskManagement/Components/Graphs'
import GraphsView from 'features/RiskManagement/Components/Graphs/Layout/LineView'
import LineViewBreadcrumb from 'features/RiskManagement/Components/LineViewBreadcrumb'
import {
  selectRiskUnit,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import RiskViewShell from 'features/RiskManagement/Components/RiskViewShell'
import {
  IGroupColumnsSelection,
  QRATableLineViewMode
} from 'features/RiskManagement/Components/Tables'
import TableView from 'features/RiskManagement/Components/Tables/Layout/LineView'
import { convertTableAPIDataToQRATableComponentProps } from 'features/RiskManagement/Components/Tables/utils/data'
import {
  formatYearColDefs,
  generateOverrideMap,
  overrideValueFormatter
} from 'features/RiskManagement/Components/Tables/utils/formatters'
import { getGraphUnits } from 'features/RiskManagement/GraphUnits'
import MapWrapper from 'features/RiskManagement/QRA/Components/MapWrapper'

import QRASaveStateButton from './Components/Buttons/QRASaveStateButton'
import CurrentStateToolbar from './Components/Toolbar/CurrentStateToolbar'

interface IBaseWrapperProps {
  qraBasePath: string
  url: string
}

interface IWrapperProps extends IBaseWrapperProps {
  lineId: string
}

const GraphsViewWrapper = ({ qraBasePath, url, lineId }: IWrapperProps) => (
  <div className='flex flex-1'>
    <OverlayScrollbar>
      <LineGraphsInit qraBasePath={qraBasePath} qraViewBasePath={url} />
    </OverlayScrollbar>
    <div className='w-1/3'>
      <MapWrapper baselineId='current' continuousRouteId={lineId} />
    </div>
  </div>
)

const TableViewWrapper = ({ qraBasePath, url }: IBaseWrapperProps) => (
  <LineTableInit qraBasePath={qraBasePath} qraViewBasePath={url} />
)

// #region Line View Main
const LineView = ({ qraBasePath }: IQRAViewProps) => {
  const { pathname } = useLocation()
  const params = useParams<{ lineId: string }>()

  const isTableView = pathname.includes(QRAViewType.Table)

  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const selectedLine = (): IContinuousRoute | undefined => {
    const tempLines = systemScopeFilterProfile?.continuousRoutes.filter(
      (route) => route.id.toString() === params.lineId
    )

    return tempLines?.[0]
  }

  const pageHeaderText = 'Current State Risk'

  const pageSubheading = (
    <LineViewBreadcrumb
      systemViewPath={`${qraBasePath}/${
        isTableView ? QRAViewType.Table : QRAViewType.Graph
      }`}
      line={selectedLine()}
      continuousRouteId={params.lineId!}
    />
  )

  return (
    <RiskViewShell
      breadcrumbs={pageSubheading}
      title={pageHeaderText}
      actions={<QRASaveStateButton />}
      toolbar={
        <CurrentStateToolbar
          qraBasePath={qraBasePath}
          qraViewBasePath={pathname}
        />
      }
    >
      <div className='flex flex-1 overflow-auto bg-gray-100'>
        <Routes>
          <Route
            path={QRAViewType.Graph}
            element={
              <GraphsViewWrapper
                qraBasePath={qraBasePath}
                url={pathname}
                lineId={params.lineId!}
              />
            }
          />
          <Route
            path={QRAViewType.Table}
            element={
              <TableViewWrapper qraBasePath={qraBasePath} url={pathname} />
            }
          />
          <Route
            path='*'
            element={
              <GraphsViewWrapper
                qraBasePath={qraBasePath}
                url={pathname}
                lineId={params.lineId!}
              />
            }
          />
        </Routes>
      </div>
    </RiskViewShell>
  )
}
// #endregion

// #region Line Graphs View
const LineGraphsInit = ({
  qraBasePath,
  qraViewBasePath
}: IGraphsViewBaseProps) => {
  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)
  const continuousRouteId = getLineIdFromQRAViewBasePath(qraViewBasePath)

  const graphSelections: IQRAGraphBools = {
    riskbythreat: true,
    riskbyconsequence: true,
    riskbyline: true,
    riskbylinearreference: true,
    riskforecast: true,
    riskbysignificancelevel: true,
    riskbysystem: true
  }

  const qraGraphsAPIParams: IGetQRAGraphsQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    graphSelections,
    continuousRouteId
  }

  const {
    currentData: charts,
    isFetching,
    isUninitialized,
    refetch
  } = useGetQRAGraphsQuery(qraGraphsAPIParams, {
    skip: activeFilterProfileIdContainer.id == null
  })

  useEffect(() => {
    if (!isUninitialized) {
      refetch()
    }
  }, [activeFilterProfileIdContainer, isUninitialized, refetch])

  return (
    <GraphsView
      qraBasePath={qraBasePath}
      qraViewBasePath={qraViewBasePath}
      showLoading={isFetching}
      charts={charts}
    />
  )
}
// #endregion

// #region Line Table View
const LineTableInit = ({
  qraViewBasePath
}: IQRANavUtilsConsumerComponentInitProps) => {
  useToggleRiskDensity()

  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)
  const continuousRouteId = getLineIdFromQRAViewBasePath(qraViewBasePath)

  const [lineViewMode, setLineViewMode] = useState<QRATableLineViewMode>(
    QRATableLineViewMode.Output
  )

  const [groupColumns, setGroupColumns] = useState<IGroupColumnsSelection[]>([
    { id: 0, name: 'Threat', groupTypeColumns: undefined }
  ])

  const isOutputTableView = lineViewMode === QRATableLineViewMode.Output

  const qraTableQueryParams: IGetQRATableQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    tableTypeSelections:
      groupColumns.length > 0 && groupColumns[0].id !== undefined
        ? groupColumns.map((group) =>
            isOutputTableView ? startCase(group.name) : group.id.toString()
          )
        : [null],
    lineViewMode: isOutputTableView ? 'Output' : 'Input',
    continuousRouteId
  }

  const {
    data: tableData,
    isFetching,
    isUninitialized,
    refetch
  } = useGetQRATableQuery(qraTableQueryParams, {
    skip: activeFilterProfileIdContainer.id == null
  })

  const [addQRATableExportJob] = useAddQRATableExportJobMutation()

  const [colDefs, rowData] = useMemo(() => {
    const [_colDefs, _rowData] = convertTableAPIDataToQRATableComponentProps(
      tableData,
      groupColumns
    )

    // more notes on this in ../Components/Tables/utils/formatters:10
    const yearOverrideMap = generateOverrideMap(
      _colDefs,
      'year',
      formatYearColDefs
    )

    const dateOverrideMap = generateOverrideMap(
      _colDefs,
      'date',
      formatYearColDefs
    )

    const filteredDefs = _colDefs.map((colDef) =>
      overrideValueFormatter(colDef, [yearOverrideMap, dateOverrideMap])
    )

    return [filteredDefs, _rowData]
  }, [groupColumns, tableData])

  useEffect(() => {
    if (!isUninitialized) {
      refetch()
    }
  }, [activeFilterProfileIdContainer, isUninitialized, refetch])

  const unitData = getGraphUnits(tableData)

  return (
    <TableView
      showLoading={isFetching}
      colDefs={colDefs}
      rowData={rowData}
      s1Label={tableData?.dataset1Rows.name}
      lineViewMode={lineViewMode}
      setLineViewMode={setLineViewMode}
      units={unitData}
      groupColumns={groupColumns}
      setGroupColumns={setGroupColumns}
      mutationTrigger={addQRATableExportJob}
      exportParams={qraTableQueryParams}
    />
  )
}
// #endregion

export default LineView
